<template>
  <div class="container">
    <!-- 侧边栏遮罩层 -->
    <div 
      v-if="isMobile" 
      class="sidebar-mask" 
      :class="{ 'show': sidebarVisible }"
      @click="toggleSidebar"
    ></div>

    <!-- 移动端侧边栏切换按钮 -->
    <div class="sidebar-toggle" v-if="isMobile" @click="toggleSidebar" @mouseenter="toggleSidebar">
        <menu-unfold-one theme="outline" size="24" fill="#352121" :strokeWidth="3"/>
    </div>

    <!-- 左侧博客文章列表 -->
    <div 
      class="docs-list" 
      :class="{ 'sidebar-collapsed': isMobile && !sidebarVisible }"
    >
      <div v-if="groups.length > 0" id="groups-container">
        <div class="group" v-for="group in groups" :key="group.id">
          <div class="group-header" @click="toggleGroup(group.id)">
            <span class="group-indicator">{{ group.visible ? '-' : '+' }}</span>
            <span class="group-name">{{ group.name }}</span>
          </div>
          <ul class="group-content" :style="{ display: group.visible ? 'block' : 'none' }">
            <li v-for="doc in getDocumentsByGroup(group.id)" :key="doc.id">
              <a 
                href="#" 
                class="doc-item" 
                :class="{ 'active': currentDocId === doc.id }"
                @click.prevent="selectDocument(doc.id); if(isMobile) toggleSidebar()"
              >
                <span class="text">{{ doc.title }}</span>
              </a>
            </li>
          </ul>
        </div>
      </div>
      <p v-else>暂无博客文章</p>
    </div>

    <!-- 右侧博客内容显示区域 -->
    <div id="preview-wrapper" class="markdown-body">
      <h1 v-if="currentDocId" class="document-title">{{ getCurrentDocumentTitle() }}</h1>
      <div class="content-wrapper">
        <div class="preview-container">
          <MdPreview 
            v-if="currentDocId" 
            :id="id" 
            :modelValue="previewContent" 
            :theme="'light'"
            :previewTheme="'vuepress'"
            :codeTheme="'atom'"
            :showCodeRowNumber="true"
            :codeFoldable="false"
            style="text-align: left;"
          />
          <div v-else class="welcome-screen">
            <h1>欢迎访问我们的博客</h1>
            <p>请选择左侧分类中的文章进行查看。</p>
          </div>
        </div>
      </div>
    </div>

    <MdCatalog 
        v-if="currentDocId"
        class="catalog"
        :editorId="id"
        :scrollElement="scrollElement" 
        @onClick="handleCatalogClick"
        style="text-align: left;"
    />

  </div>
</template>

<script>
import { MdPreview , MdCatalog} from 'md-editor-v3';
import 'md-editor-v3/lib/style.css';
import { MenuUnfoldOne } from '@icon-park/vue-next';

export default {
  id : 'markdown-view',
  name: 'BlogView',
  components: {
    MdPreview,
    MdCatalog,
    MenuUnfoldOne
  },

  data() {
    return {
      documents: [],
      groups: [],
      previewContent: '',
      currentDocId: null,
      id: 'my-editor',
      scrollElement: null,
      modalVisible: false,
      modalTitle: '',
      modalMessage: '',
      modalConfirmText: '确定',
      isMobile: false,
      sidebarVisible: false
    }
  },
  async mounted() {
    try {
      // 先加载分组数据
      await this.loadGroups();
      // 再加载文档数据
      await this.loadDocuments();

      // 文档列表加载完成后，再尝试恢复之前选中的文档
      const savedDocId = localStorage.getItem('selectedBlogDocumentId');
      if (savedDocId) {
        // console.log('发现已保存的文档ID:', savedDocId);
        // 检查保存的文档ID是否在当前文档列表中（直接比较字符串）
        const docExists = this.documents.some(doc => doc.id === savedDocId);
        // console.log('文档是否存在:', docExists);
        if (docExists) {
          // console.log('正在恢复选中的文档:', savedDocId);
          this.selectDocument(savedDocId);
        } else {
          // console.log('保存的文档ID在列表中不存在，显示第一个文档');
          this.showFirstDocument();
        }
      } else {
        this.showFirstDocument();
      }
    } catch (error) {
      console.error('Error during mounted:', error);
      // 如果任何加载步骤失败，确保显示欢迎页
      this.showFirstDocument();
    }

    // 设置滚动元素
    this.$nextTick(() => {
      // 这里 scrollElement 必须是与 MdCatalog 同级的 preview-wrapper ，不同级元素，滚动位置不正确
      this.scrollElement = document.querySelector('.preview-wrapper');
    });

    // 初始化移动端检测
    this.checkIsMobile();
    window.addEventListener('resize', this.checkIsMobile);
  },
  beforeDestroy() {
    window.removeEventListener('resize', this.checkIsMobile);
  },
  methods: {
    checkIsMobile() {
      this.isMobile = window.innerWidth <= 768;
      // 在移动端，sidebar默认隐藏
      if (this.isMobile) {
        this.sidebarVisible = false;
      }
    },
    
    toggleSidebar() {
      this.sidebarVisible = !this.sidebarVisible;
    },

    async loadGroups() {
      try {
        // 调用后端API获取分组数据
        const response = await fetch('/api/blog/groups', {
          method: 'GET',
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('authToken')}`,
            'Content-Type': 'application/json'
          }
        });
        
        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }
        
        const data = await response.json();
        // 处理空分组情况，确保至少显示Default分组
        if (data.length === 0) {
          this.groups = [
            { id: 1, name: 'Default', visible: true }
          ];
        } else {
          this.groups = data.map(group => ({
            id: group.id,
            name: group.name,
            visible: true
          }));
          // 产品文档排最前
          const idx = this.groups.findIndex(g => g.name === '产品文档');
          if (idx > 0) {
            const item = this.groups.splice(idx, 1)[0];
            this.groups.unshift(item);
          }
        }
        
        // 恢复分组的展开/折叠状态
        this.loadGroupStates();
      } catch (error) {
        console.error('Error loading groups:', error);
        // 如果出错，使用Default
        this.groups = [
          { id: 1, name: 'Default', visible: true }
        ];
        // 恢复分组的展开/折叠状态
        this.loadGroupStates();
      }
    },
    
    async loadDocuments() {
      try {
        // 调用后端API获取文档列表数据（只获取元数据）
        const response = await fetch('/api/blog/documents/', {
          method: 'GET',
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('authToken')}`,
            'Content-Type': 'application/json'
          }
        });
        
        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }
        
        const data = await response.json();
        this.documents = data.map(doc => ({
          id: doc.id,
          title: doc.title,
          group_id: doc.group_id
        }));
      } catch (error) {
        console.error('Error loading document list:', error);
        this.documents = [];
      }
    },
    getDocumentsByGroup(groupId) {
      const group = this.groups.find(g => g.id === groupId);
      if (!group) return [];
      const groupName = group.name.trim().toLowerCase();
      return this.documents.filter(doc => 
        doc.group_id.trim().toLowerCase() === groupName
      );
    },
    toggleGroup(groupId) {
      const group = this.groups.find(g => g.id === groupId);
      if (group) {
        group.visible = !group.visible;
        // 保存分组状态到localStorage
        this.saveGroupStates();
      }
    },
    // 保存分组状态到localStorage
    saveGroupStates() {
      const groupStates = {};
      this.groups.forEach(group => {
        groupStates[group.id] = group.visible;
      });
      localStorage.setItem('blogGroupStates', JSON.stringify(groupStates));
    },
    
    // 从localStorage加载分组状态
    loadGroupStates() {
      try {
        const groupStates = JSON.parse(localStorage.getItem('blogGroupStates'));
        if (groupStates) {
          this.groups.forEach(group => {
            if (Object.prototype.hasOwnProperty.call(groupStates, group.id)) {
              group.visible = groupStates[group.id];
            }
          });
        }
      } catch (error) {
        console.error('Error loading group states:', error);
      }
    },
    
    async selectDocument(docId) {
      // 实现选择文档并预览逻辑
      console.debug('Select document', docId);
      
      try {
        // 调用后端API获取具体文档内容
        const response = await fetch(`/api/blog/document?id=${encodeURIComponent(docId)}`, {
          method: 'GET',
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('authToken')}`,
            'Content-Type': 'application/json'
          }
        });
        
        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }
        
        const data = await response.json();
        
        this.currentDocId = docId;
        this.previewContent = data.body || '';
        // 保存当前选中的文档ID到localStorage
        localStorage.setItem('selectedBlogDocumentId', docId);
      } catch (error) {
        console.error('Error loading document content:', error);
        this.currentDocId = null;
        this.previewContent = '# 加载失败\n\n无法加载文章内容，请稍后重试。';
        // 清除localStorage中的文档ID
        localStorage.removeItem('selectedBlogDocumentId');
      }
    },

  
    showFirstDocument() {
      // 优先显示第一个可用文档（如果存在）
      if (this.documents && this.documents.length > 0) {
        this.selectDocument(this.documents[0].id);
      } else {
        // 没有文档时显示欢迎信息
        this.currentDocId = null;
        this.previewContent = '';
      }
    },
    
    getCurrentDocumentTitle() {
      if (!this.currentDocId) return '';
      const doc = this.documents.find(d => d.id === this.currentDocId);
      return doc ? doc.title : '';
    },
    handleCatalogClick(e, tocItem) {
      console.debug('点击了目录项:', tocItem);
      console.debug('标题文本:', tocItem.text);
      console.debug('应滚动到的位置:', tocItem.top);

      // 查找所有标题 h1-h6
      const headers = document.querySelectorAll('.preview-container h1, .preview-container h2, .preview-container h3, .preview-container h4, .preview-container h5, .preview-container h6');
      let targetElement = null;
      const want = (tocItem.text || '').trim();

      // helper: 去掉可能的前置数字序号，如 "1. ", "2 "
      const normalize = (s) => s.replace(/^\s*\d+[).]?\s*/, '').trim();

      for (let header of headers) {
        const txt = header.textContent.trim();
        if (txt === want) {
          targetElement = header;
          break;
        }
        if (normalize(txt) === normalize(want)) {
          targetElement = header;
          break;
        }
        if (txt.includes(want) || want.includes(txt)) {
          targetElement = header;
          break;
        }
      }

      // 动态计算偏移量
      const navbar = document.querySelector('nav');
      const navbarHeight = navbar ? navbar.offsetHeight : 60;
      const container = document.querySelector('.preview-container');
      const containerStyle = container ? getComputedStyle(container) : null;
      const containerPaddingTop = containerStyle ? parseFloat(containerStyle.paddingTop) : 20;
      const extraOffset = 10;
      const offset = navbarHeight + containerPaddingTop + extraOffset;

      if (targetElement) {
        console.debug('找到目标元素：', targetElement);
        const container = document.querySelector('.preview-container');
        const containerRect = container.getBoundingClientRect();
        const targetRect = targetElement.getBoundingClientRect();
        const top = container.scrollTop + (targetRect.top - containerRect.top) - offset;
        console.debug('滚动到容器 top:', top);
        container.scrollTo({ top, behavior: 'smooth' });
        return;
      }

      // 回退：使用 tocItem.top（如果可用）
      if (tocItem.top !== undefined) {
        const container = document.querySelector('.preview-container');
        const top = tocItem.top - offset;
        container.scrollTo({ top, behavior: 'smooth' });
      } else {
        console.warn('未找到匹配的标题元素，也没有 tocItem.top 可用。');
      }
    }
  }
}
</script>

<style scoped>
.container {
  display: flex;
  height: calc(100vh - 120px);
  width: 90vw;
  max-width: 90%;
  margin: 20px auto;
  border: 1px solid #ddd;
  border-radius: 8px;
  overflow: hidden;
  box-shadow: 0 0 10px rgba(0, 0, 0, 0.1);
}

.docs-list {
  flex: 2;
  background-color: #f8f9fa;
  padding: 20px;
  overflow-y: auto;
  border-right: 1px solid #ddd;
}

.group {
  margin-bottom: 15px;
}

.group-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 10px;
  background-color: #e9ecef;
  border-radius: 4px;
  cursor: pointer;
  user-select: none;
}

.group-header:hover {
  background-color: #dee2e6;
}

.group-indicator {
  font-weight: bold;
  font-size: 18px;
  color: #333;
  padding: 0 5px;
}

.group-name {
  font-weight: bold;
  font-size: 16px;
  color: #333;
  flex: 1;
  max-width: 70%;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  padding-right: 10px;
}

.group-content {
  list-style-type: none;
  padding: 0;
  margin: 10px 0 0 0;
}

.doc-item {
  display: flex;
  justify-content: space-between;
  align-items: center;
  text-decoration: none;
  color: #333;
  transition: background-color 0.3s ease;
  padding: 8px 12px;
  background-color: #f9f9f9;
  border: 1px solid #ddd;
  border-radius: 4px;
  margin-bottom: 5px;
  cursor: pointer;
  /* 固定高度 */
  height: 40px;
  /* 限制文本显示 */
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.doc-item:hover,
.doc-item.active {
  color: #ff5f5f;
  background-color: #f0f0f0;
  /* 鼠标悬停时显示完整文本 */
  overflow: visible;
  white-space: normal;
  z-index: 1;
  position: relative;
}

#preview-wrapper {
  flex: 7;
  padding: 5px 10px 10px 10px;
  display: flex;
  flex-direction: column;
  align-items: center;
  background-color: var(--background);
  height: 100%;
  overflow: hidden; /* 防止预览包装器滚动 */
  /* border: 2px solid blue; /* 调试用边框 */
}

.document-title {
  text-align: center;
  margin: 0 0 20px 0;
  color: #333;
}

.content-wrapper {
  display: flex;
  width: 100%;
  height: calc(100vh - 200px);
  gap: 20px;
  border-radius: var(--border-radius);
  box-shadow: var(--box-shadow);
  overflow: hidden;
}

.preview-container {
  flex: 1;
  overflow-y: auto;
  padding: 20px;
  background: #fff;
  border-radius: var(--border-radius);
  box-shadow: var(--box-shadow);
}

/* 美化滚动条样式 - Webkit浏览器 */
.preview-container::-webkit-scrollbar {
  width: 8px;
}

.preview-container::-webkit-scrollbar-track {
  background: #f1f1f1;
  border-radius: 4px;
}

.preview-container::-webkit-scrollbar-thumb {
  background: #c1c1c1;
  border-radius: 4px;
}

.preview-container::-webkit-scrollbar-thumb:hover {
  background: #a8a8a8;
}

.catalog {
  flex: 1;
  min-width: 180px;
  padding: 20px;
  box-sizing: border-box;
  overflow-y: auto;
  border-left: 1px solid rgba(0,0,0,0.06);
  background: var(--background);
  border-radius: var(--border-radius);
  box-shadow: var(--box-shadow);
}

    /* 手机端响应式样式 */
@media (max-width: 768px) {
  .container {
    flex-direction: column;
    height: calc(100vh - 60px);
    width: 100vw;
    max-width: none;
    margin: 0;
    border-radius: 0;
  }
  
  .docs-list {
    position: fixed;
    top: 60px;
    left: 0;
    width: 80%;
    max-width: 300px;
    height: calc(100vh - 60px);
    z-index: 10000;
    box-shadow: 2px 0 5px rgba(0,0,0,0.1);
    transform: translateX(-100%);
    transition: transform 0.3s ease;
    background-color: #f8f9fa;
    overflow-y: auto;
  }
  
  .docs-list.sidebar-collapsed {
    transform: translateX(-100%);
  }
  
  .docs-list:not(.sidebar-collapsed) {
    transform: translateX(0);
  }
  
  .sidebar-toggle {
    position: fixed;
    top: 50%;
    left: 0;
    transform: translateY(-50%);
    width: 30px;
    height: 30px;
    background-color: #e9ecef;
    border: 1px solid #ddd;
    border-radius: 4px;
    display: flex;
    justify-content: center;
    align-items: center;
    cursor: pointer;
    z-index: 10001;
  }
  
  .sidebar-mask {
    position: fixed;
    top: 60px;
    left: 0;
    width: 100%;
    height: calc(100vh - 60px);
    background-color: rgba(0,0,0,0.5);
    z-index: 9999;
    opacity: 0;
    visibility: hidden;
    transition: all 0.3s ease;
  }
  
  .sidebar-mask.show {
    opacity: 1;
    visibility: visible;
  }
  
  #preview-wrapper {
    width: 100%;
    flex: 1;
    height: auto;
  }
  
  .catalog {
    display: none;
  }
  
  .group-content {
    display: none;
  }
  
  .group-header {
    cursor: pointer;
  }

  #preview-wrapper {
    padding: 0;
    align-items: flex-start;
  }

  .content-wrapper {
    gap: 0;
  }

  .preview-container {
    padding: 0px;
  }
  
  /* 确保代码复制按钮不会遮挡侧边栏 */
  .preview-container :deep(.md-editor-preview-wrapper) {
    position: relative;
    z-index: 1;
  }
  
  .preview-container :deep(.md-editor-preview-wrapper code) {
    position: relative;
    z-index: 1;
  }
  
  .preview-container :deep(.md-editor-preview-wrapper pre) {
    position: relative;
    z-index: 1;
  }
  
  /* 提高复制代码按钮的容器层级 */
  .preview-container :deep(.md-editor-preview-wrapper .code-block-wrapper) {
    z-index: 1;
  }
  
  /* 提高复制代码按钮本身的层级 */
  .preview-container :deep(.md-editor-preview-wrapper .copy-btn) {
    z-index: 1;
  }
}
/* 美化滚动条样式 - Webkit浏览器 */
.catalog::-webkit-scrollbar {
  width: 8px;
}

.catalog::-webkit-scrollbar-track {
  background: #f1f1f1;
  border-radius: 4px;
}

.catalog::-webkit-scrollbar-thumb {
  background: #c1c1c1;
  border-radius: 4px;
}

.catalog::-webkit-scrollbar-thumb:hover {
  background: #a8a8a8;
}

.welcome-screen {
  text-align: center;
  padding: 50px;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  height: 100%;
}

.welcome-screen h1 {
  color: #333;
  margin-bottom: 20px;
}

.welcome-screen p {
  color: #666;
  margin-bottom: 10px;
}
</style>